import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {AuthenticationService} from './authentication.service';
import {IndexConfigService} from './index.config.service';
import {Load} from '../_decorators/loading.decorator';
import {EvidenceCentralItem} from '../_models/evidenceCentralItem';
import {LoginHelper} from '../helpers';
import {FixDates} from "../_decorators/date.decorator";

@Injectable()
export class EvidenceCentralService {
  constructor(
    public http: HttpClient,
    public authenticationService: AuthenticationService,
    public config: IndexConfigService
  ) {
  }

  @Load()
  @FixDates()
  getAll(): Observable<EvidenceCentralItem[]> {
    if (!this.canExecute()) {
      return null;
    }

    return this.http
      .get<EvidenceCentralItem[]>(this.config.api + '/api/evidenceCentral')
      .pipe(
        map((response) => {
          return response.map(this.mapEvidenceCentral);
        })
      );
  }

  @Load()
  @FixDates()
  getById(id: number): Observable<EvidenceCentralItem> {
    if (!this.canExecute()) {
      return null;
    }

    return this.http
      .get<EvidenceCentralItem>(this.config.api + `/api/evidenceCentral/${id}`)
      .pipe(map(this.mapEvidenceCentral));
  }

  @Load()
  @FixDates()
  add(evidenceCentralItem: EvidenceCentralItem): Observable<EvidenceCentralItem> {
    if (!this.canExecute()) {
      return null;
    }

    const payload = {
      ...evidenceCentralItem,
      settings: evidenceCentralItem.settings
        ? JSON.stringify(evidenceCentralItem.settings)
        : undefined,
    };

    return this.http
      .post<EvidenceCentralItem>(this.config.api + `/api/evidenceCentral`, payload)
      .pipe(map(this.mapEvidenceCentral));
  }

  @Load()
  @FixDates()
  update(evidenceCentralItem: EvidenceCentralItem) {
    if (!this.canExecute()) {
      return null;
    }
    const payload = {
      ...evidenceCentralItem,
      settings: evidenceCentralItem.settings
        ? JSON.stringify(evidenceCentralItem.settings)
        : undefined,
    };

    return this.http.patch<EvidenceCentralItem>(
      this.config.api + `/api/evidenceCentral/${evidenceCentralItem.id}`,
      payload
    );
  }

  @Load()
  delete(id: number) {
    if (!this.canExecute()) {
      return null;
    }

    return this.http.delete(this.config.api + `/api/evidenceCentral/${id}`);
  }

  @Load()
  @FixDates()
  test(evidenceCentralItem: EvidenceCentralItem) {
    return this.http.post(
      this.config.api + `/api/evidenceCentral/${evidenceCentralItem.id}/test`,
      evidenceCentralItem
    );
  }

  @Load()
  @FixDates()
  testFile(evidenceCentralItem: any): Observable<any> {
    if (!this.canExecute()) {
      return null;
    }

    const payload = {
      ...evidenceCentralItem,
      settings: evidenceCentralItem.settings
        ? JSON.stringify(evidenceCentralItem.settings)
        : undefined,
    };

    return this.http
      .post<EvidenceCentralItem>(this.config.api + `/api/evidenceCentral/testFile`, payload)
      .pipe(map(this.mapEvidenceCentral));
  }

  private canExecute() {
    if (
      LoginHelper.getTenantName() == null ||
      LoginHelper.getUserToken() == null
    ) {
      this.authenticationService.logout();

      return false;
    }

    return true;
  }

  private mapEvidenceCentral(evidenceCentralItem: EvidenceCentralItem) {
    let data = {...evidenceCentralItem};

    if (evidenceCentralItem?.schedule) {
      const startDateTime = !isNaN(
        Date.parse(evidenceCentralItem.schedule.startDateTime as any as string)
      )
        ? new Date(evidenceCentralItem.schedule.startDateTime)
        : evidenceCentralItem.schedule.startDateTime;
      const endDateTime = !isNaN(
        Date.parse(evidenceCentralItem.schedule.endDateTime as any as string)
      )
        ? new Date(evidenceCentralItem.schedule.endDateTime)
        : evidenceCentralItem.schedule.endDateTime;

      const schedule = {...evidenceCentralItem.schedule, startDateTime, endDateTime};

      data.schedule = schedule;
    }

    if (evidenceCentralItem?.settings) {
      data.settings = JSON.parse(evidenceCentralItem.settings);
    }

    return data;
  }
}
